// The agent registry: one source for the Agents menu, search, the Agents page and the agent tiles (shell-ia §3.2; DESIGN-SYSTEM §8,
// §10.3). Names, glyphs, routes and gates are static; status is never static: it comes from `GET /api/econic/modules/status` (WP-MODKIT),
// or from the shell bundle when it carries it. WP-SHELL.
import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { ApiError, useApi, useAuth } from "@ncp/api-client";
import type { ModuleKey, ModuleStatus } from "@ncp/assistant-sdk/contracts";
import { ECONIC_MODULES, agentNames } from "./theme/primitives";
import { useShellSummary } from "./shell/useShellSummary";
import { useRollout } from "./trust/api";

export interface ModuleDef {
  key: ModuleKey;
  /** Rail label and full name (DESIGN-SYSTEM §8, SPEC §13), EN and AR. */
  short: { en: string; ar: string };
  name: { en: string; ar: string };
  /** The persona, the agent's first name (agentNames); none for Policy (PMO). */
  persona?: { en: string; ar: string };
  icon: LucideIcon;
  route: string;
  /** Any one of these opens the module (app/routes.tsx). */
  permission: string[];
  badge?: "foundation" | "priority";
  /** The rail's route prefixes that light this module. */
  match: string[];
}

const ROUTE: Record<ModuleKey, { route: string; permission: string[]; badge?: ModuleDef["badge"]; match: string[] }> = {
  kb: { route: "/knowledge", permission: ["knowledge.read"], badge: "foundation", match: ["/knowledge", "/library"] },
  "uae-radar": { route: "/m/uae-radar", permission: ["economics.read"], match: ["/m/uae-radar", "/evidence"] },
  "global-radar": { route: "/m/global-radar", permission: ["economics.read"], match: ["/m/global-radar"] },
  outlook: { route: "/m/outlook", permission: ["econic.outlook.read"], match: ["/m/outlook"] },
  country360: { route: "/m/country360", permission: ["econic.country360.read"], badge: "priority", match: ["/m/country360"] },
  policylab: { route: "/m/policylab", permission: ["econic.policylab.read"], match: ["/m/policylab"] },
};

/** The six agents in the Ministry's order: Knowledge Bank · Economic Radar · Country360 · Policy (PMO) · Economic Modelling · News Watch. */
export const MODULES: readonly ModuleDef[] = ECONIC_MODULES.map((m) => ({
  key: m.key as ModuleKey,
  short: m.short,
  name: m.name,
  persona: m.persona,
  icon: m.icon,
  ...ROUTE[m.key as ModuleKey],
}));

export const moduleByKey = (key: string): ModuleDef | undefined => MODULES.find((m) => m.key === (key === "knowledge" ? "kb" : key));

/** An agent's two names: the persona first, the product under it (packages/ui econic/modules). */
export { agentNames };

/** The module a path belongs to, for the rail's active chip. */
export function moduleOfPath(pathname: string): ModuleKey | null {
  for (const m of MODULES) if (m.match.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return m.key;
  return null;
}

export interface ModuleStatusState {
  byKey: Partial<Record<ModuleKey, ModuleStatus>>;
  loading: boolean;
  /** The endpoint failed or is not served: chips read "Status unavailable", never "Live" (shell-ia §5.2). */
  unavailable: boolean;
}

const normaliseKey = (k: string): ModuleKey => (k === "knowledge" ? "kb" : (k as ModuleKey));

function index(list: ModuleStatus[] | null | undefined): Partial<Record<ModuleKey, ModuleStatus>> {
  const out: Partial<Record<ModuleKey, ModuleStatus>> = {};
  for (const s of list ?? []) out[normaliseKey(s.key)] = { ...s, key: normaliseKey(s.key) };
  return out;
}

/** Module counts, due flags and source health as the viewer may see them. */
export function useModuleStatus(): ModuleStatusState {
  const api = useApi();
  const { isAuthenticated, user } = useAuth();
  const shell = useShellSummary();
  const bundled = shell.data?.modules;
  const q = useQuery({
    queryKey: ["econic", "modules", "status", user?.id ?? null],
    queryFn: () => api.get<ModuleStatus[] | { modules: ModuleStatus[] }>("/api/econic/modules/status"),
    enabled: isAuthenticated && !bundled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: (n, e) => !(e instanceof ApiError && (e.status === 404 || e.status === 405)) && n < 1,
  });
  if (bundled) return { byKey: index(bundled), loading: false, unavailable: false };
  const list = Array.isArray(q.data) ? q.data : q.data?.modules;
  return { byKey: index(list), loading: q.isLoading, unavailable: q.isError };
}

/**
 * The modules the viewer is shown (WP-TRUST's rollout, ECN-098): a module the DT Administrator has not rolled out to this person's groups
 * leaves the Intelligence menu, the segmented row, search and the phone sheet. Rollout only hides; the routes still check permissions.
 * While the rollout endpoint does not answer, every module stays listed.
 */
export function useVisibleModules(): ReadonlySet<ModuleKey> {
  const q = useRollout();
  const rows = q.data?.modules;
  if (!rows?.length) return ALL_KEYS;
  const hidden = new Set(rows.filter((r) => r.visible === false).map((r) => normaliseKey(r.module)));
  return hidden.size ? new Set(MODULES.map((m) => m.key).filter((k) => !hidden.has(k))) : ALL_KEYS;
}
const ALL_KEYS: ReadonlySet<ModuleKey> = new Set(MODULES.map((m) => m.key));
